import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { ManagerActions } from './manager.actions';
import { ManagerApiService } from '../../predictor/services/manager-api-service';
import { catchError, tap, throwError } from 'rxjs';
import { patch, removeItem, updateItem } from '@ngxs/store/operators';
import { toasty } from '../../global/operators/ngxs-rxjs-toast';
import { StudyArea } from 'src/app/predictor/interfaces/study-area';

export interface ManagerStateModel {
  studyAreas: StudyArea[];
  totalRecords: number;
  continuationTokens: string[];
  loadingStudyAreas: boolean;
  pageIndex: number;
  pageSize: number
}

const stateDefaults: ManagerStateModel = {
  studyAreas: [],
  loadingStudyAreas: false,
  continuationTokens: [''],
  pageIndex: 0,
  totalRecords: 0,
  pageSize: 10
};

@State<ManagerStateModel>({
  name: 'Manager',
  defaults: stateDefaults,
})
@Injectable()
export class ManagerState {
  constructor(private managerApiService: ManagerApiService) { }
  @Selector()
  static studyAreas(UserStateModel: ManagerStateModel): any[] {
    return UserStateModel.studyAreas;
  }

  @Selector()
  static loadingStudyAreas(UserStateModel: ManagerStateModel): boolean {
    return UserStateModel.loadingStudyAreas;
  }

  @Selector()
  static totalRecords(UserStateModel: ManagerStateModel): number {
    return UserStateModel.totalRecords || 0;
  }

  @Action(ManagerActions.ChangePageIndex)
  changePageIndex(ctx: StateContext<ManagerStateModel>, { pageIndex }: ManagerActions.ChangePageIndex) {
    ctx.patchState({ pageIndex: pageIndex });
  }

  @Action(ManagerActions.ChangePageSize)
  changePageSize(ctx: StateContext<ManagerStateModel>, { pageSize }: ManagerActions.ChangePageSize) {
    ctx.patchState({ pageSize: pageSize, pageIndex: 0, continuationTokens: [''] });
  }

  @Action(ManagerActions.GetStudyAreas)
  getStudyAreas(ctx: StateContext<ManagerStateModel>) {
    ctx.patchState({ loadingStudyAreas: true });
    const continuationTokens: string[] = ctx.getState().continuationTokens;
    const index: number = ctx.getState().pageIndex;
    const pageSize: number = ctx.getState().pageSize;

    return this.managerApiService.getStudyAreas(continuationTokens[index], pageSize).pipe(
      tap((result) => {
        if (continuationTokens.length <= index + 1) {
          continuationTokens.push(result.nextContinuationToken!);
        }
        ctx.patchState({
          studyAreas: result.items.$values,
          loadingStudyAreas: false,
          totalRecords: result.totalRecords,
        });
      })
    );
  }

  @Action(ManagerActions.GetAllStudyAreas)
  getAllStudyAreas(ctx: StateContext<ManagerStateModel>) {
    ctx.patchState({ loadingStudyAreas: true });

    return this.managerApiService.getAllStudyAreas().pipe(
      tap((result) => {
        ctx.patchState({
          studyAreas: result,
          loadingStudyAreas: false,
          totalRecords: result.length,
        });
      })
    );
  }

  @Action(ManagerActions.DeleteStudyArea)
  deleteStudyArea(
    ctx: StateContext<ManagerStateModel>,
    { studyArea }: ManagerActions.DeleteStudyArea
  ) {
    return this.managerApiService.deleteStudyArea(studyArea.id).pipe(
      toasty(ManagerActions.DeleteStudyArea, ctx.dispatch),
      tap(() => {
        ctx.setState(
          patch({
            studyAreas: removeItem<any>((study) => study.id === studyArea.id),
          })
        );
      })
    );
  }

  @Action(ManagerActions.UpdateStudyAreaStatus)
  updateStudyArea(
    ctx: StateContext<ManagerStateModel>,
    { studyArea, status, callbackOnError, callbackOnSuccess }: ManagerActions.UpdateStudyAreaStatus
  ) {
    const newStudyArea: any = { ...studyArea, status: status };

    return this.managerApiService
      .updatePaidStatus({
        status: status,
        email: newStudyArea.requestor.email,
        studyAreaName: studyArea.studyAreaName,
        projectCode: studyArea.projectCode,
        invoiceId: studyArea.invoiceId,
      })
      .pipe(
        toasty(ManagerActions.UpdateStudyAreaStatus, ctx.dispatch),
        tap(() => {
          ctx.setState(
            patch({
              studyAreas: updateItem<any>(
                (study) =>
                  study.studyAreaName === studyArea.studyAreaName &&
                  study.requestor.email === studyArea.requestor.email,
                patch(newStudyArea)
              ),
            })
          );
          if (callbackOnSuccess) {
            callbackOnSuccess();
          }
        }),
        catchError((error) => {
          console.error("Failed to update study area:", error);
          if (callbackOnError) {
            callbackOnError(error.message || "Unknown error");
          }
          return throwError(() => error);
        })
      );
  }

  @Action(ManagerActions.UpdateStudyAreaComment)
  updateStudyAreaComment(
    ctx: StateContext<ManagerStateModel>,
    { request }: ManagerActions.UpdateStudyAreaComment
  ) {
    return this.managerApiService.updateStudyAreaComment(request).pipe(
      toasty(ManagerActions.UpdateStudyAreaComment, ctx.dispatch),
      tap(() => {
        ctx.setState(
          patch({
            studyAreas: updateItem<StudyArea>(
              (study) => study.id === request.studyAreaId,
              patch({ comment: request }),
            ),
          })
        );

        if(request.invoiceId) {
          ctx.setState(
            patch({
              studyAreas: updateItem<StudyArea>(
                (study) => study.id === request.studyAreaId,
                patch({ invoiceId: request.invoiceId }),
              ),
            })
          );
        }
        if(request.projectCode) {
          ctx.setState(
            patch({
              studyAreas: updateItem<StudyArea>(
                (study) => study.id === request.studyAreaId,
                patch({ projectCode: request.projectCode }),
              ),
            })
          );
        }
        if(request.content=="" || request.content==null) {
          ctx.setState(
            patch({
              studyAreas: updateItem<StudyArea>(
                (study) => study.id === request.studyAreaId,
                patch({ comment: null}),
              ),
            })
          );
        }
      })
    );
  }
}
